import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, getDocs, addDoc } from 'firebase/firestore';
import AdminHeader from '../components/AdminHeader';
import { useAuth } from '../AuthContext';

// Quick Grade Entry Table Component
const GradeEntryTable = ({ classes }) => {
  const [gradeData, setGradeData] = useState({});
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);

  // Initialize grade data for all classes
  useEffect(() => {
    const initialData = {};
    classes.forEach(className => {
      initialData[className] = {
        formativePercent: '',
        summativePercent: '',
        notes: ''
      };
    });
    setGradeData(initialData);
  }, [classes]);

  const handleInputChange = (className, field, value) => {
    setGradeData(prev => ({
      ...prev,
      [className]: {
        ...prev[className],
        [field]: value
      }
    }));
  };

  const handleSubmitAll = async () => {
    try {
      const promises = [];

      for (const [className, data] of Object.entries(gradeData)) {
        const formativePercent = parseFloat(data.formativePercent);
        const summativePercent = parseFloat(data.summativePercent);

        // Skip if no data entered for this class
        if (isNaN(formativePercent) || isNaN(summativePercent)) {
          continue;
        }

        // Calculate overall grade (35% formative + 65% summative)
        const overallPercent = (formativePercent * 0.35) + (summativePercent * 0.65);

        promises.push(
          addDoc(collection(db, 'grades'), {
            date,
            className,
            formativePercent,
            summativePercent,
            overallPercent,
            notes: data.notes || '',
            createdAt: new Date().toISOString()
          })
        );
      }

      if (promises.length > 0) {
        await Promise.all(promises);
        alert(`Successfully added grades for ${promises.length} class(es)!`);

        // Reset form
        const resetData = {};
        classes.forEach(className => {
          resetData[className] = {
            formativePercent: '',
            summativePercent: '',
            notes: ''
          };
        });
        setGradeData(resetData);
      } else {
        alert('Please enter complete grade data for at least one class.');
      }
    } catch (error) {
      console.error('Error submitting grades:', error);
      alert('Error submitting grades. Please try again.');
    }
  };

  return (
    <div className="space-y-4">
      {/* Date Selector */}
      <div className="flex items-center space-x-4 mb-4">
        <label className="text-sm font-medium">Date:</label>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="p-2 bg-[#212121] border border-[#4d4d4d] rounded text-white focus:outline-none focus:border-blue-500"
        />
      </div>

      {/* Grade Entry Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-[#4d4d4d]">
              <th className="text-left py-3 px-4 font-medium">Class</th>
              <th className="text-center py-3 px-4 font-medium text-blue-400">Formative (35%)</th>
              <th className="text-center py-3 px-4 font-medium text-red-400">Summative (65%)</th>
              <th className="text-center py-3 px-4 font-medium text-green-400">Overall (Auto)</th>
              <th className="text-left py-3 px-4 font-medium">Notes</th>
            </tr>
          </thead>
          <tbody>
            {classes.map((className, index) => {
              const formativePercent = parseFloat(gradeData[className]?.formativePercent || 0);
              const summativePercent = parseFloat(gradeData[className]?.summativePercent || 0);
              const overallPercent = !isNaN(formativePercent) && !isNaN(summativePercent) && formativePercent > 0 && summativePercent > 0
                ? ((formativePercent * 0.35) + (summativePercent * 0.65)).toFixed(1)
                : '';

              return (
                <tr key={className} className="border-b border-[#2a2a2a] hover:bg-[#1a1a1a]">
                  <td className="py-3 px-4 font-medium">{className}</td>
                  <td className="py-3 px-4">
                                            <input
                          type="number"
                          value={gradeData[className]?.formativePercent || ''}
                          onChange={(e) => handleInputChange(className, 'formativePercent', e.target.value)}
                          className="w-full p-2 bg-[#212121] border border-[#4d4d4d] rounded text-white text-center focus:outline-none focus:border-blue-500"
                          placeholder="85.5"
                          min="0"
                          max="100"
                          step="0.01"
                        />
                  </td>
                  <td className="py-3 px-4">
                    <input
                      type="number"
                      value={gradeData[className]?.summativePercent || ''}
                      onChange={(e) => handleInputChange(className, 'summativePercent', e.target.value)}
                      className="w-full p-2 bg-[#212121] border border-[#4d4d4d] rounded text-white text-center focus:outline-none focus:border-red-500"
                      placeholder="92.3"
                      min="0"
                      max="100"
                      step="0.01"
                    />
                  </td>
                  <td className="py-3 px-4 text-center">
                    <div className="p-2 bg-[#1a1a1a] border border-[#4d4d4d] rounded text-green-400 font-bold">
                      {overallPercent ? `${overallPercent}%` : '--'}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <input
                      type="text"
                      value={gradeData[className]?.notes || ''}
                      onChange={(e) => handleInputChange(className, 'notes', e.target.value)}
                      className="w-full p-2 bg-[#212121] border border-[#4d4d4d] rounded text-white focus:outline-none focus:border-gray-500"
                      placeholder="Optional notes..."
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSubmitAll}
          className="px-6 py-3 bg-green-600 hover:bg-green-700 rounded font-medium transition-colors"
        >
          Save All Grades
        </button>
      </div>
    </div>
  );
};

const AdminSchoolDashboard = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Check authentication
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin/login');
    }
  }, [isAuthenticated, navigate]);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const [grades, setGrades] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [hoverIndex, setHoverIndex] = useState(null);

  const classes = [
    'Advanced Biology F25',
    'Adv Algebra 2 - Rose',
    'Schwarz: English II',
    'Spanish 1 - Fall 2025',
    'Adv Design Applications'
  ];

  // Fetch grades and assignments for overview
  const fetchOverviewData = async () => {
    try {
      // Fetch grades
      const gradesSnapshot = await getDocs(collection(db, 'grades'));
      const gradesList = gradesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setGrades(gradesList);

      // Fetch assignments
      const assignmentsSnapshot = await getDocs(collection(db, 'assignments'));
      const assignmentsList = assignmentsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAssignments(assignmentsList);
    } catch (error) {
      console.error('Error fetching overview data:', error);
    }
  };

  // Calculate class statistics
  const calculateClassStats = (className) => {
    const classGrades = grades.filter(grade => grade.className === className);
    const classAssignments = assignments.filter(assignment => assignment.className === className);

    if (classGrades.length === 0) return null;

    // Sort by date (most recent first) and get the latest grade entry
    const sortedGrades = [...classGrades].sort((a, b) => new Date(b.date) - new Date(a.date));
    const latestGrade = sortedGrades[0];

    // Use the latest grade's overallPercent instead of averaging
    const overallAvg = latestGrade.overallPercent;

    // Keep averages for formative and summative (or could use latest too)
    const formativeAvg = classGrades.reduce((sum, grade) => sum + grade.formativePercent, 0) / classGrades.length;
    const summativeAvg = classGrades.reduce((sum, grade) => sum + grade.summativePercent, 0) / classGrades.length;

    return {
      formativeAvg: formativeAvg.toFixed(1),
      summativeAvg: summativeAvg.toFixed(1),
      overallAvg: overallAvg.toFixed(1),
      totalGrades: classGrades.length,
      activeAssignments: classAssignments.filter(a => a.status !== 'completed').length
    };
  };

  // Generate chart data for a class (all historical data)
  const generateChartData = (className) => {
    const classGrades = grades
      .filter(grade => grade.className === className)
      .sort((a, b) => new Date(a.date) - new Date(b.date));

    return classGrades.map(grade => ({
      date: new Date(grade.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      formative: grade.formativePercent,
      summative: grade.summativePercent,
      overall: grade.overallPercent
    }));
  };

  // Fetch data on component mount
  useEffect(() => {
    if (isAuthenticated) {
      fetchOverviewData();
    }
  }, [isAuthenticated]);

  // Multi-Line Chart Component for overlaid grade trends
  const MultiLineChart = ({ data, series, height = 120 }) => {
    if (!data || data.length === 0) {
      return (
        <div className="flex items-center justify-center text-gray-500 text-xs" style={{height: height/3}}>
          No data yet
        </div>
      );
    }

    // Calculate chart dimensions and dynamic scaling (ignore missing values)
    const allValues = series
      .flatMap(s => data.map(d => d[s.key]))
      .filter(v => typeof v === 'number' && !isNaN(v));

    if (allValues.length === 0) {
      return (
        <div className="flex items-center justify-center text-gray-500 text-xs" style={{height: height/3}}>
          No data yet
        </div>
      );
    }

    const rawMin = Math.min(...allValues);
    const rawMax = Math.max(...allValues);
    const span = Math.max(0.0001, rawMax - rawMin);
    const paddingAmount = Math.max(2, span * 0.1);
    let yMin = Math.max(0, rawMin - paddingAmount);
    let yMax = Math.min(100, rawMax + paddingAmount);
    const minRange = 20;
    if (yMax - yMin < minRange) {
      const needed = minRange - (yMax - yMin);
      const half = needed / 2;
      yMin = Math.max(0, yMin - half);
      yMax = Math.min(100, yMax + half);
    }
    // Round to nice ticks
    yMin = Math.floor(yMin / 5) * 5;
    yMax = Math.ceil(yMax / 5) * 5;
    if (yMax <= yMin) {
      yMax = Math.min(100, yMin + minRange);
    }

    const chartWidth = 300;
    const chartHeight = height;
    const padding = 8;

    // Helpers
    const getX = (index) => {
      if (data.length === 1) return padding + (chartWidth - 2 * padding) / 2;
      return (index / (data.length - 1)) * (chartWidth - 2 * padding) + padding;
    };
    const getY = (value) => chartHeight - padding - ((value - yMin) / (yMax - yMin)) * (chartHeight - 2 * padding);
    const handleMouseMove = (e) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const relativeX = e.clientX - rect.left - padding;
      const plotWidth = chartWidth - 2 * padding;
      const clamped = Math.max(0, Math.min(plotWidth, relativeX));
      const idx = data.length > 1 ? Math.round((clamped / plotWidth) * (data.length - 1)) : 0;
      setHoverIndex(idx);
    };
    const handleMouseLeave = () => setHoverIndex(null);

    return (
      <div className="relative flex items-start">
        <svg
          width={chartWidth}
          height={chartHeight}
          className="overflow-visible"
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          {/* Grid lines */}
          <defs>
            <pattern id="multiline-grid" width="20" height="20" patternUnits="userSpaceOnUse">
              <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#4d4d4d" strokeWidth="0.5" opacity="0.2"/>
            </pattern>
          </defs>
          <rect width={chartWidth} height={chartHeight} fill="url(#multiline-grid)" />

          {/* Render each series */}
          {series.map((s, seriesIndex) => {
            // Build contiguous segments where values exist to avoid drawing lines through missing points
            const segments = [];
            let current = [];
            data.forEach((point, index) => {
              const value = point[s.key];
              if (typeof value === 'number' && !isNaN(value)) {
                current.push({ index, value });
              } else if (current.length > 0) {
                segments.push(current);
                current = [];
              }
            });
            if (current.length > 0) segments.push(current);

            return (
              <g key={s.key}>
                {segments.map((seg, segIdx) => {
                  const points = seg.map(({ index, value }) => `${getX(index)},${getY(value)}`).join(' ');
                  return (
                    <polyline
                      key={`${s.key}-seg-${segIdx}`}
                      points={points}
                      fill="none"
                      stroke={s.color}
                      strokeWidth="2.5"
                      strokeLinejoin="round"
                      strokeLinecap="round"
                      opacity="0.8"
                    />
                  );
                })}

                {/* Data points for existing values only */}
                {data.map((point, index) => {
                  const value = point[s.key];
                  if (typeof value !== 'number' || isNaN(value)) return null;
                  const x = getX(index);
                  const y = getY(value);
                  return (
                    <circle
                      key={`${s.key}-${index}`}
                      cx={x}
                      cy={y}
                      r="4"
                      fill={s.color}
                      stroke="#0d0d0d"
                      strokeWidth="1.5"
                      opacity="0.9"
                    />
                  );
                })}
              </g>
            );
          })}

          {/* Hover vertical line */}
          {hoverIndex != null && (
            <line
              x1={getX(hoverIndex)}
              y1={padding}
              x2={getX(hoverIndex)}
              y2={chartHeight - padding}
              stroke="#888"
              strokeDasharray="4 3"
              opacity="0.7"
            />
          )}
        </svg>

        {/* Legend moved outside to the right of the graph */}
        <div className="ml-3 flex flex-col space-y-1">
          {series.map(s => (
            <div key={s.key} className="flex items-center space-x-2 text-xs">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: s.color }}
              ></div>
              <span style={{ color: s.color }}>{s.label}</span>
            </div>
          ))}
        </div>

        {/* Hover tooltip */}
        {hoverIndex != null && hoverIndex < data.length && (
          <div
            className="absolute pointer-events-none bg-[#1a1a1a] border border-[#4d4d4d] rounded p-2 text-xs shadow"
            style={{
              left: Math.min(Math.max(getX(hoverIndex) + 8, 0), chartWidth - 170),
              top: 0
            }}
          >
            <div className="font-semibold mb-1">{data[hoverIndex]?.date}</div>
            {series.map(s => {
              const v = data[hoverIndex]?.[s.key];
              const hasValue = typeof v === 'number' && !isNaN(v);
              return (
                <div key={`tip-${s.key}`} className="flex items-center justify-between space-x-3">
                  <div className="flex items-center space-x-2">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: s.color }}></div>
                    <span className="text-gray-300">{s.label}</span>
                  </div>
                  <span className="text-white">{hasValue ? `${v.toFixed(1)}%` : '--'}</span>
                </div>
              );
            })}
          </div>
        )}

        {/* Date range below chart (not overlaying) */}
        <div className="absolute left-0 right-0 -bottom-6 flex justify-between text-xs text-gray-400">
          <span>{data[0]?.date}</span>
          <span>{data[data.length - 1]?.date}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#0d0d0d] text-white">
      <AdminHeader />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold mb-2">School Dashboard</h1>
          <p className="text-gray-400">Academic management and progress tracking</p>
        </div>
        <div className="flex space-x-4">
          <Link
            to="/admin/assignment-tracker"
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded font-medium transition-colors"
          >
            📝 Assignments
          </Link>
          <Link
            to="/admin/progress-analytics"
            className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded font-medium transition-colors"
          >
            📈 Analytics
          </Link>
          <Link
            to="/admin/full-charts"
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded font-medium transition-colors"
          >
            📊 Full Charts
          </Link>
        </div>
      </div>

      {/* Class Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {classes.map(className => {
          const stats = calculateClassStats(className);
          return (
            <div key={className} className="bg-[#1a1a1a] border border-[#4d4d4d] rounded-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold">{className}</h3>
                {stats ? (
                  <span className="text-xl font-bold text-green-400">{stats.overallAvg}%</span>
                ) : (
                  <span className="text-sm text-gray-500">No data</span>
                )}
              </div>
              {stats ? (
                <>
                  <div className="flex justify-between text-sm text-gray-400 mb-4">
                    <span>{stats.totalGrades} entries</span>
                    <span>{stats.activeAssignments} active assignments</span>
                  </div>

                  {/* Combined Trend Chart */}
                  <div className="pt-3 border-t border-[#4d4d4d]">
                    <div className="text-xs text-gray-400 mb-2">Grade Trends (All Data)</div>
                    <div className="flex justify-center">
                      <MultiLineChart
                        data={generateChartData(className)}
                        series={[
                          { key: 'formative', label: 'Formative', color: '#60a5fa' },
                          { key: 'summative', label: 'Summative', color: '#f87171' },
                          { key: 'overall', label: 'Overall', color: '#34d399' }
                        ]}
                        height={100}
                      />
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-6 text-gray-400">
                  <div className="text-2xl mb-2">📊</div>
                  <div className="text-sm">No grade data yet</div>
                  <div className="text-xs text-gray-500 mt-1">Use the table below to add grades</div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Quick Grade Entry */}
      <div className="bg-[#1a1a1a] border border-[#4d4d4d] rounded-lg p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold">Quick Grade Entry</h2>
          <Link
            to="/admin/progress-analytics"
            className="text-blue-400 hover:text-blue-300 text-sm"
          >
            View Full Analytics →
          </Link>
        </div>

        <GradeEntryTable classes={classes} />
      </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSchoolDashboard;
